import {
  Button,
  Checkbox,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextField,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { useInsideMapsConfig } from "../hooks/useInsideMapsConfig";
import { InsideMapsConfigStore } from "../store/InsideMapsConfigStore";
import { MaterialVariantOption } from "../types";

interface BulkMaterialAssignmentButtonProps {
  materialVariants: MaterialVariantOption[];
}

export const BulkMaterialAssignmentButton = observer(function BulkMaterialAssignmentButton({
  materialVariants,
}: BulkMaterialAssignmentButtonProps) {
  const store = useInsideMapsConfig();
  const { openModal } = useModal();
  return (
    <button
      css={Css.pyPx(8).pxPx(16).br4.white.smSb.cursorPointer.add("background", "#4f46e5").add("border", "none").$}
      onClick={() =>
        openModal({
          content: <BulkMaterialAssignmentDialog materialVariants={materialVariants} store={store} />,
          size: "lg",
        })
      }
    >
      Bulk Assign Materials
    </button>
  );
});

interface BulkMaterialAssignmentDialogProps {
  materialVariants: MaterialVariantOption[];
  store: InsideMapsConfigStore;
}

const BulkMaterialAssignmentDialog = observer(function BulkMaterialAssignmentDialog({
  materialVariants,
  store,
}: BulkMaterialAssignmentDialogProps) {
  const tid = useTestIds({});
  const { closeModal } = useModal();
  const [mvSearch, setMvSearch] = useState("");
  const [selectedMvId, setSelectedMvId] = useState<string | null>(null);
  const [selectedTextures, setSelectedTextures] = useState<Set<string>>(new Set());

  const allTextures = useMemo(() => {
    const result: Array<{
      key: string;
      cameraId: string;
      cameraName: string;
      partId: string;
      partName: string;
      textureIndex: number;
      textureName: string;
    }> = [];
    for (const camera of store.cameras) {
      for (const part of camera.parts) {
        for (let i = 0; i < part.textures.length; i++) {
          const key = `${camera.id}|${part.id}|${i}`;
          result.push({
            key,
            cameraId: camera.id,
            cameraName: camera.cameraName,
            partId: part.id,
            partName: part.name,
            textureIndex: i,
            textureName: part.textures[i].name,
          });
        }
      }
    }
    return result;
  }, [store.cameras]);

  const filteredMaterials = useMemo(() => {
    if (!mvSearch) return materialVariants.slice(0, 50);
    const tokens = mvSearch.toLowerCase().split(/\s+/).filter(Boolean);
    return materialVariants
      .filter((mv) => {
        const text = `${mv.displayName} ${mv.id}`.toLowerCase();
        return tokens.every((t) => text.includes(t));
      })
      .slice(0, 50);
  }, [materialVariants, mvSearch]);

  const handleToggleTexture = useCallback((key: string) => {
    setSelectedTextures((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleApply = useCallback(() => {
    if (!selectedMvId) return;
    const assignments = allTextures
      .filter((t) => selectedTextures.has(t.key))
      .map((t) => ({ cameraId: t.cameraId, partId: t.partId, textureIndex: t.textureIndex, mvIds: [selectedMvId] }));
    store.bulkSetMaterialVariantIds(assignments);
    closeModal();
  }, [store, selectedMvId, selectedTextures, allTextures, closeModal]);

  return (
    <>
      <ModalHeader>Bulk Material Assignment</ModalHeader>
      <ModalBody>
        <div css={Css.df.gap3.$} {...tid.bulkAssignment}>
          {/* Material selection */}
          <div css={Css.w50.$}>
            <div css={Css.smSb.mb1.$}>Select Material</div>
            <TextField
              label="Search materials..."
              labelStyle="hidden"
              value={mvSearch}
              onChange={(val) => setMvSearch(val ?? "")}
              placeholder="Search materials..."
            />
            <div css={Css.maxhPx(300).oa.mt1.$}>
              {filteredMaterials.map((mv) => (
                <div
                  key={mv.id}
                  css={
                    Css.df.aic.gap2
                      .pyPx(6)
                      .pxPx(8)
                      .br4.cursorPointer.sm.if(selectedMvId === mv.id)
                      .bgBlue50.ba.bcBlue300.if(selectedMvId !== mv.id).ba.bcGray100.$
                  }
                  onClick={() => setSelectedMvId(mv.id)}
                >
                  {mv.imageUrl && (
                    <img
                      src={mv.imageUrl}
                      alt={mv.displayName}
                      css={Css.wPx(32).hPx(32).br4.add("objectFit", "cover").$}
                    />
                  )}
                  <div>
                    <div css={Css.sm.$}>{mv.displayName}</div>
                    <div css={Css.xs.gray500.$}>{mv.id}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Texture selection */}
          <div css={Css.w50.$}>
            <div css={Css.smSb.mb1.$}>Select Textures ({selectedTextures.size})</div>
            <div css={Css.maxhPx(350).oa.$}>
              {allTextures.map((t) => (
                <div key={t.key} css={Css.df.aic.gap1.xs.pyPx(2).$}>
                  <Checkbox
                    label={`${t.cameraName} / ${t.partName} / ${t.textureName}`}
                    selected={selectedTextures.has(t.key)}
                    onChange={() => handleToggleTexture(t.key)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Apply" disabled={!selectedMvId || selectedTextures.size === 0} onClick={handleApply} />
      </ModalFooter>
    </>
  );
});
